import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Icon from './Icon'
import { formatRub } from '../utils/formatRub'
import { useEmployeeNotificationsOptional } from '../contexts/EmployeeNotificationsContext'
import './EmployeeActionBanners.css'

// Договоры и бюджеты стадий образуют одну очередь; одинаковые ID разных типов
// не смешиваются, а ссылки остаются теми, которые вернул API.
function ApprovalCarousel ({ pendingContractApprovals, pendingBudgetApprovals }) {
  const approvals = [
    ...pendingContractApprovals.map((item) => ({
      key: `contract:${item.contract_id}`,
      subject: [item.object_name?.trim(), item.contract_number?.trim()].filter(Boolean).join(' — ') || 'договору',
      path: item.report_path || `/contract/${item.contract_id}/report-preview`,
    })),
    ...pendingBudgetApprovals.map((item) => ({
      key: `budget:${item.budget_id || item.report_path || item.contract_id}`,
      subject: [item.object_name?.trim(), item.stage_label?.trim()].filter(Boolean).join(' — ') || item.contract_number || 'бюджету',
      path: item.report_path || `/contract/${item.contract_id}/report-preview`,
    })),
  ]
  const [selection, setSelection] = useState({ key: null, index: 0 })
  const retainedIndex = approvals.findIndex((item) => item.key === selection.key)
  const index = retainedIndex >= 0 ? retainedIndex : Math.min(selection.index, Math.max(0, approvals.length - 1))
  const current = approvals[index]
  const currentKey = current?.key || null

  // Фиксируем выбранный элемент после обновления очереди: удалённый элемент
  // не должен снова становиться текущим при последующей загрузке данных.
  useEffect(() => {
    setSelection((previous) => previous.key === currentKey && previous.index === index
      ? previous
      : { key: currentKey, index })
  }, [currentKey, index])

  if (!current) return null

  const move = (direction) => {
    const nextIndex = (index + direction + approvals.length) % approvals.length
    setSelection({ key: approvals[nextIndex].key, index: nextIndex })
  }

  return (
    <section className="employee-approval-banner" aria-label="Согласования бюджета">
      <span className="employee-approval-banner__icon" aria-hidden>
        <Icon name="file-check" size={18} />
      </span>
      <div className="employee-approval-banner__body" role="status" aria-atomic="true">
        <span className="employee-approval-banner__title">Требуется согласование бюджета</span>
        <span className="employee-approval-banner__text">
          Согласуйте бюджет по <strong>{current.subject}</strong>
        </span>
      </div>
      <div className="employee-approval-banner__actions">
        {approvals.length > 1 && (
          <div className="employee-approval-banner__navigation" aria-label="Переключение согласований">
            <button type="button" className="ui-btn employee-approval-banner__arrow" aria-label="Предыдущее согласование" onClick={() => move(-1)}>
              <Icon name="chevronLeft" size={18} aria-hidden />
            </button>
            <span className="employee-approval-banner__counter" aria-live="polite" aria-atomic="true">{index + 1} из {approvals.length}</span>
            <button type="button" className="ui-btn employee-approval-banner__arrow" aria-label="Следующее согласование" onClick={() => move(1)}>
              <Icon name="chevronRight" size={18} aria-hidden />
            </button>
          </div>
        )}
        <Link to={current.path} className="employee-approval-banner__cta">
          Открыть отчёт
          <Icon name="chevronRight" size={14} aria-hidden />
        </Link>
      </div>
    </section>
  )
}

function EmployeeActionBanners () {
  const notifications = useEmployeeNotificationsOptional()
  if (!notifications) return null

  const { pendingPayoutRequest, pendingContractApprovals, pendingBudgetApprovals, openPayoutModal } = notifications
  const hasPayoutBanner = Boolean(pendingPayoutRequest)
  const hasContractBanners = pendingContractApprovals.length > 0
  const hasBudgetBanners = pendingBudgetApprovals.length > 0

  if (!hasPayoutBanner && !hasContractBanners && !hasBudgetBanners) return null

  return (
    <div className="employee-action-banners">
      {hasPayoutBanner && (
        <button
          type="button"
          className="payout-wish-banner"
          onClick={() => openPayoutModal(pendingPayoutRequest)}
        >
          <span className="payout-wish-banner__icon" aria-hidden>
            <Icon name="mail" size={18} />
          </span>
          <span className="payout-wish-banner__body">
            <span className="payout-wish-banner__title">Квартал завершён — заполни сумму к получению</span>
            <span className="payout-wish-banner__text">
              За {pendingPayoutRequest.period_label} вы заработали{' '}
              <strong>{formatRub(pendingPayoutRequest.earned, { showZero: true })}</strong>. Решите, сколько хотите получить в этом квартале.
            </span>
          </span>
          <span className="payout-wish-banner__cta">
            Заполнить
            <Icon name="chevronRight" size={14} />
          </span>
        </button>
      )}
      {(hasContractBanners || hasBudgetBanners) && (
        <ApprovalCarousel
          pendingContractApprovals={pendingContractApprovals}
          pendingBudgetApprovals={pendingBudgetApprovals}
        />
      )}
    </div>
  )
}

export default EmployeeActionBanners
